/**
 * GA4 Analytics Utilities
 *
 * Set the `VITE_GA4_MEASUREMENT_ID` env var (e.g., G-XXXXXXXXXX) to enable tracking.
 * When unset, all functions no-op gracefully so dev builds stay silent.
 */

const MEASUREMENT_ID =
  import.meta.env.VITE_GA4_MEASUREMENT_ID ||
  import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY ||
  "";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let installed = false;

/** Inject the GA4 gtag.js script once */
export function initGA4(): void {
  if (installed || !MEASUREMENT_ID || typeof window === "undefined") return;
  installed = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };

  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/** Track a page view (call on route change) */
export function trackPageview(path: string): void {
  if (!window.gtag || !MEASUREMENT_ID) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
    send_to: MEASUREMENT_ID,
  });
}

/** Track a custom event (e.g., generate_lead) */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (!window.gtag || !MEASUREMENT_ID) return;
  window.gtag("event", eventName, params);
}

/**
 * Conversion helpers
 * ------------------
 * Every high-intent action on the site funnels through one of these so the
 * event names stay consistent in GA4 (and so we can mark them as key events).
 */

/** Phone tap / click — the single highest-intent action on a care site. */
export function trackCallClick(location: string): void {
  trackEvent("contact_call_click", { link_location: location });
  trackEvent("generate_lead", { lead_type: "phone_call", form_name: "phone", link_location: location });
}

/** Email link click. */
export function trackEmailClick(location: string): void {
  trackEvent("contact_email_click", { link_location: location });
}

/** Any click that leaves the site. */
export function trackOutboundClick(url: string, location: string): void {
  trackEvent("outbound_click", { link_url: url.slice(0, 200), link_location: location });
}

/** Primary CTA clicks (Request Care / Consultation / Apply). */
export function trackCtaClick(ctaName: string, location: string): void {
  trackEvent("cta_click", { cta_name: ctaName.slice(0, 100), link_location: location });
}

/** Checkout started for a paid product. */
export function trackBeginCheckout(itemId: string, value: number): void {
  trackEvent("begin_checkout", { currency: "USD", value, item_id: itemId });
}

/** Completed purchase (fired once on the thank-you page). */
export function trackPurchase(transactionId: string, itemId: string, value: number): void {
  trackEvent("purchase", { transaction_id: transactionId, currency: "USD", value, item_id: itemId });
}

/** Scroll-depth engagement milestones — the bounce-rate signal. */
export function trackScrollDepth(percent: number, path: string): void {
  trackEvent("scroll_depth", { percent, page_path: path });
}
